import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { fetchTenders, triggerMatching, embedTender } from '../../api/tenders';
import type { Tender, TenderStatus } from '../../types';
import StatusBadge from '../../components/StatusBadge';

export default function TenderList() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [tenders, setTenders] = useState<Tender[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<TenderStatus | ''>('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const dateLocale = i18n.resolvedLanguage === 'de' ? 'de-DE' : 'en-GB';

  const STATUS_OPTIONS: Array<{ value: TenderStatus | ''; labelKey: string }> = [
    { value: '', labelKey: 'tenders.allStatuses' },
    { value: 'draft',     labelKey: 'tenders.draft' },
    { value: 'submitted', labelKey: 'tenders.submitted' },
    { value: 'matching',  labelKey: 'tenders.matching' },
    { value: 'matched',   labelKey: 'tenders.matched' },
    { value: 'won',       labelKey: 'tenders.won' },
    { value: 'lost',      labelKey: 'tenders.lost' },
    { value: 'archived',  labelKey: 'tenders.archived' },
  ];

  const loadTenders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchTenders({
        page,
        page_size: 20,
        search: search || undefined,
        status: statusFilter || undefined,
        sort_by: 'created_at',
        order: 'desc',
      });
      setTenders(result.items);
      setTotalPages(result.total_pages);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('tenders.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, t]);

  useEffect(() => {
    loadTenders();
  }, [loadTenders]);

  async function handleEmbed(tenderId: string, tenderTitle: string) {
    if (!window.confirm(t('tenders.confirmEmbed', { title: tenderTitle }))) return;
    setActionLoading(tenderId);
    try {
      await embedTender(tenderId);
      alert(t('tenders.embedSuccess'));
    } catch (err) {
      alert(err instanceof Error ? err.message : t('tenders.embedFailed'));
    } finally {
      setActionLoading(null);
    }
  }

  async function handleTriggerMatch(tenderId: string, tenderTitle: string) {
    if (!window.confirm(t('tenders.confirmMatch', { title: tenderTitle }))) return;
    setActionLoading(tenderId);
    try {
      await embedTender(tenderId).catch(() => {/* embedding may already exist */});
      await triggerMatching(tenderId);
      await loadTenders();
    } catch (err) {
      alert(err instanceof Error ? err.message : t('tenders.matchFailed'));
    } finally {
      setActionLoading(null);
    }
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('tenders.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">{t('tenders.subtitle')}</p>
        </div>
        <Link to="/tenders/submit" className="btn-primary self-start sm:self-auto">
          {t('tenders.submitTender')}
        </Link>
      </header>

      {/* Filters */}
      <section aria-label={t('tenders.filters')} className="flex flex-wrap gap-3">
        <div className="flex-1 min-w-48">
          <label htmlFor="tender-search" className="sr-only">{t('tenders.searchLabel')}</label>
          <input
            id="tender-search"
            type="search"
            placeholder={t('tenders.searchPlaceholder')}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="form-input"
            aria-label={t('tenders.searchLabel')}
          />
        </div>
        <div className="min-w-[180px]">
          <label id="status-filter-label" className="sr-only">{t('tenders.filterByStatus')}</label>
          <Select
            aria-labelledby="status-filter-label"
            options={STATUS_OPTIONS.map((opt) => ({ value: opt.value, label: t(opt.labelKey) }))}
            value={{ value: statusFilter, label: t(STATUS_OPTIONS.find((o) => o.value === statusFilter)?.labelKey ?? 'tenders.allStatuses') }}
            onChange={(opt) => { setStatusFilter((opt?.value ?? '') as TenderStatus | ''); setPage(1); }}
            classNamePrefix="react-select"
            isSearchable={false}
            styles={{
              control: (base) => ({ ...base, fontSize: '0.875rem', minHeight: '38px' }),
              option: (base) => ({ ...base, fontSize: '0.875rem' }),
            }}
          />
        </div>
        <button onClick={loadTenders} className="btn-secondary" aria-label={t('common.refresh')}>
          {t('common.refresh')}
        </button>
      </section>

      {/* Error alert */}
      {error && (
        <div role="alert" className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div role="status" aria-live="polite" className="flex items-center gap-3 text-gray-500 py-8">
          <span className="animate-spin text-brand-600 text-xl" aria-hidden="true">⟳</span>
          {t('tenders.loading')}
        </div>
      )}

      {/* Tender table */}
      {!loading && !error && (
        <>
          {tenders.length === 0 ? (
            <div className="card text-center py-12">
              <p className="text-gray-500 text-sm">{t('tenders.noTenders')}</p>
              <Link to="/tenders/submit" className="btn-primary mt-4 inline-flex">
                {t('tenders.submitFirst')}
              </Link>
            </div>
          ) : (
            <div className="card overflow-hidden p-0">
              <div className="overflow-x-auto">
                <table className="data-table" aria-label={t('tenders.tenderList')}>
                  <thead>
                    <tr>
                      <th scope="col">{t('tenders.titleCol')}</th>
                      <th scope="col">{t('tenders.createdAtCol', 'Created')}</th>
                      <th scope="col">{t('tenders.overdueCol', 'Overdue')}</th>
                      <th scope="col">{t('tenders.locationCol')}</th>
                      <th scope="col">{t('tenders.sectorCol')}</th>
                      <th scope="col">{t('tenders.skillsRequired')}</th>
                      <th scope="col">{t('common.status')}</th>
                      <th scope="col"><span className="sr-only">{t('common.actions')}</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenders.map((tender) => (
                      <tr key={tender.id}>
                        <td className="font-medium text-gray-900 max-w-xs truncate">
                          <button
                            onClick={() => navigate(`/tenders/${tender.id}/matches`)}
                            className="text-left hover:text-brand-700 focus:outline-none focus-visible:underline"
                            aria-label={t('tenders.viewDetails', { title: tender.title })}
                          >
                            {tender.title}
                          </button>
                        </td>
                        <td className="text-gray-500">
                          <time dateTime={tender.created_at}>
                            {new Date(tender.created_at).toLocaleDateString(dateLocale, {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </time>
                        </td>
                        <td className="text-center">
                          {(() => {
                            const created = new Date(tender.created_at);
                            const hoursSince = (Date.now() - created.getTime()) / (1000 * 60 * 60);
                            const isOverdue = hoursSince > 48;
                            return isOverdue ? (
                              <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700" aria-label={t('tenders.overdueYes', 'Overdue')}>
                                {t('tenders.overdueYes', 'Overdue')}
                              </span>
                            ) : (
                              <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700" aria-label={t('tenders.onTime', 'On time')}>
                                {t('tenders.onTime', 'On time')}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="text-sm text-gray-500">
                          {tender.work_location || <span className="text-gray-300">—</span>}
                        </td>
                        <td className="text-sm text-gray-500">
                          {tender.sector || <span className="text-gray-300">—</span>}
                        </td>
                        <td>
                          <span className="text-xs text-gray-500">
                            {tender.required_skills.slice(0, 3).join(', ')}
                            {tender.required_skills.length > 3 && ` +${tender.required_skills.length - 3}`}
                          </span>
                        </td>
                        <td>
                          <StatusBadge status={tender.status} />
                        </td>
                        <td>
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              onClick={() => handleEmbed(tender.id, tender.title)}
                              disabled={actionLoading === tender.id}
                              className="text-xs text-gray-600 hover:text-gray-800 disabled:opacity-40 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-500 rounded"
                              aria-label={t('tenders.generateEmbedding', { title: tender.title })}
                            >
                              {actionLoading === tender.id ? t('tenders.working') : t('tenders.embed')}
                            </button>
                            <button
                              onClick={() => handleTriggerMatch(tender.id, tender.title)}
                              disabled={actionLoading === tender.id}
                              className="text-xs text-brand-600 hover:text-brand-800 disabled:opacity-40 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-500 rounded"
                              aria-label={t('tenders.runAiMatching', { title: tender.title })}
                            >
                              {actionLoading === tender.id ? t('tenders.running') : t('tenders.runMatch')}
                            </button>
                            <Link
                              to={`/tenders/${tender.id}/matches`}
                              className="text-xs text-brand-600 hover:text-brand-800 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-500 rounded"
                              aria-label={t('tenders.viewMatchesFor', { title: tender.title })}
                            >
                              {t('tenders.results')}
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <nav aria-label={t('tenders.pagination')} className="flex items-center justify-center gap-3">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-secondary"
                aria-label={t('common.previous')}
              >
                &larr; {t('common.previous')}
              </button>
              <span className="text-sm text-gray-600" aria-current="page">
                {t('common.pageOf', { page, totalPages })}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="btn-secondary"
                aria-label={t('common.next')}
              >
                {t('common.next')} &rarr;
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
}
